import { CHARACTER_ACTIONS, type CharacterActionKey } from "@/character/actions";
import {
  SKILL_TEMPLATE_FRAME_HEIGHT,
  SKILL_TEMPLATE_FRAME_WIDTH,
} from "@/character/skills/constants";
import {
  GRID_COLUMNS,
  SHEET_COLUMNS,
  TEMPLATE_FRAME_HEIGHT,
  TEMPLATE_FRAME_WIDTH,
  TEMPLATE_SHEET_HEIGHT,
  TEMPLATE_SHEET_WIDTH,
  SPRITE_SHEET_WIDTH,
} from "@/character/grid";

const FULL_TEMPLATE_FILENAME = "mind-survival-character-sheet-template.png";

function drawSpriteCells(
  ctx: CanvasRenderingContext2D,
  originX: number,
  originY: number,
): void {
  for (let col = 0; col < GRID_COLUMNS; col += 1) {
    const x = originX + col * TEMPLATE_FRAME_WIDTH;
    ctx.fillStyle = col % 2 === 0 ? "#121a24" : "#0f1419";
    ctx.fillRect(x, originY, TEMPLATE_FRAME_WIDTH, TEMPLATE_FRAME_HEIGHT);

    ctx.strokeStyle = "#2a3548";
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, originY + 0.5, TEMPLATE_FRAME_WIDTH - 1, TEMPLATE_FRAME_HEIGHT - 1);

    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = "#3d4f66";
    ctx.strokeRect(
      x + 8,
      originY + 8,
      TEMPLATE_FRAME_WIDTH - 16,
      TEMPLATE_FRAME_HEIGHT - 16,
    );
    ctx.setLineDash([]);
  }
}

function drawLabelCell(
  ctx: CanvasRenderingContext2D,
  y: number,
  title: string,
  subtitle: string,
): void {
  ctx.fillStyle = "#0f1419";
  ctx.fillRect(0, y, TEMPLATE_FRAME_WIDTH, TEMPLATE_FRAME_HEIGHT);
  ctx.strokeStyle = "#2a3548";
  ctx.strokeRect(0.5, y + 0.5, TEMPLATE_FRAME_WIDTH - 1, TEMPLATE_FRAME_HEIGHT - 1);

  const pillX = 10;
  const pillY = y + 20;
  const pillW = TEMPLATE_FRAME_WIDTH - 20;
  const pillH = 54;

  ctx.fillStyle = "#1a2332";
  ctx.beginPath();
  ctx.roundRect(pillX, pillY, pillW, pillH, 10);
  ctx.fill();
  ctx.strokeStyle = "#3d4f66";
  ctx.stroke();

  ctx.fillStyle = "#6ec8ff";
  ctx.font = "bold 11px system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(title, pillX + 8, pillY + 8);

  ctx.fillStyle = "#8b9cb3";
  ctx.font = "9px system-ui, sans-serif";
  ctx.fillText(subtitle, pillX + 8, pillY + 26);
}

export function buildFullTemplateDataUrl(): string {
  const canvas = document.createElement("canvas");
  canvas.width = TEMPLATE_SHEET_WIDTH;
  canvas.height = TEMPLATE_SHEET_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("สร้างเทมเพลตไม่สำเร็จ");
  }

  ctx.fillStyle = "#05080c";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  for (const action of CHARACTER_ACTIONS) {
    const y = action.row * TEMPLATE_FRAME_HEIGHT;
    drawLabelCell(ctx, y, action.templateLabel, `เฟรม 1–${GRID_COLUMNS}`);
    drawSpriteCells(ctx, TEMPLATE_FRAME_WIDTH, y);
  }

  ctx.fillStyle = "#8b9cb3";
  ctx.font = "11px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(
    `กริด ${SHEET_COLUMNS} คอลัมน์ (คอลัมน์ 1 = ป้าย · 2–7 = สไปรต์) · ${TEMPLATE_SHEET_WIDTH}×${TEMPLATE_SHEET_HEIGHT} px`,
    canvas.width / 2,
    canvas.height - 8,
  );

  return canvas.toDataURL("image/png");
}

export function buildMotionTemplateDataUrl(actionKey: CharacterActionKey): string {
  const action = CHARACTER_ACTIONS.find((item) => item.key === actionKey);
  if (!action) {
    throw new Error("ไม่พบ action");
  }

  const canvas = document.createElement("canvas");
  canvas.width = SPRITE_SHEET_WIDTH;
  canvas.height = TEMPLATE_FRAME_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("สร้างเทมเพลตไม่สำเร็จ");
  }

  ctx.fillStyle = "#05080c";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  drawSpriteCells(ctx, 0, 0);

  ctx.fillStyle = "#8b9cb3";
  ctx.font = "11px system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(`${action.templateLabel} · เฟรม 1–${GRID_COLUMNS}`, 8, 12);

  return canvas.toDataURL("image/png");
}

function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
}

export function downloadCharacterTemplate(): void {
  downloadDataUrl(buildFullTemplateDataUrl(), FULL_TEMPLATE_FILENAME);
}

const SKILL_TEMPLATE_FILENAME = "mind-survival-skill-6f-72x144.png";

function drawSkillSpriteCells(ctx: CanvasRenderingContext2D, originY: number): void {
  const fw = SKILL_TEMPLATE_FRAME_WIDTH;
  const fh = SKILL_TEMPLATE_FRAME_HEIGHT;
  for (let col = 0; col < GRID_COLUMNS; col += 1) {
    const x = col * fw;
    ctx.fillStyle = col % 2 === 0 ? "#121a24" : "#0f1419";
    ctx.fillRect(x, originY, fw, fh);
    ctx.strokeStyle = "#2a3548";
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, originY + 0.5, fw - 1, fh - 1);
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = "#3d4f66";
    ctx.strokeRect(x + 6, originY + 6, fw - 12, fh - 12);
    ctx.setLineDash([]);
  }
}

export function buildSkillMotionTemplateDataUrl(): string {
  const fw = SKILL_TEMPLATE_FRAME_WIDTH;
  const fh = SKILL_TEMPLATE_FRAME_HEIGHT;
  const canvas = document.createElement("canvas");
  canvas.width = GRID_COLUMNS * fw;
  canvas.height = fh;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("สร้างเทมเพลตไม่สำเร็จ");
  }
  ctx.fillStyle = "#05080c";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  drawSkillSpriteCells(ctx, 0);
  ctx.fillStyle = "#8b9cb3";
  ctx.font = "10px system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(`SKILL ${fw}×${fh}px · 6 เฟรม`, 6, 11);
  return canvas.toDataURL("image/png");
}

export function downloadSkillMotionTemplate(): void {
  downloadDataUrl(buildSkillMotionTemplateDataUrl(), SKILL_TEMPLATE_FILENAME);
}

export function downloadMotionTemplate(actionKey: CharacterActionKey): void {
  const action = CHARACTER_ACTIONS.find((item) => item.key === actionKey);
  if (!action) return;
  const filename = `mind-survival-${action.templateLabel.toLowerCase()}-6f.png`;
  downloadDataUrl(buildMotionTemplateDataUrl(actionKey), filename);
}
